// Shared mappings for JoinLine's kind badge + JoinKindDropdown:
// label / badge class / aria label per render-side JoinKind, the
// render-side ↔ SR-Edit JoinKind translation, and the dialect filter
// for which kinds are selectable per engine.
//
// Splitting this out keeps JoinLine.tsx + JoinKindDropdown.tsx
// react-refresh-friendly (component files only export components) and
// keeps the 200-line budget per file honest.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — change JOIN kind.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { JoinKind as SrEditJoinKind } from '../../../../sql-resolver/types/queryStructure';
import type { JoinKind as RenderJoinKind } from './extractFromJoins';

export const KIND_LABELS: Record<RenderJoinKind, string> = {
  INNER: 'INNER JOIN',
  LEFT: 'LEFT JOIN',
  RIGHT: 'RIGHT JOIN',
  FULL: 'FULL JOIN',
  CROSS: 'CROSS JOIN',
  CROSS_APPLY: 'CROSS APPLY',
  OUTER_APPLY: 'OUTER APPLY',
};

export const KIND_BADGE_CLASS: Record<RenderJoinKind, string> = {
  INNER: 'bg-primary text-primary-foreground border-transparent',
  LEFT: 'bg-blue-500 text-white border-transparent',
  RIGHT: 'bg-cyan-600 text-white border-transparent',
  FULL: 'bg-purple-500 text-white border-transparent',
  CROSS: 'bg-muted text-muted-foreground border-transparent',
  CROSS_APPLY: 'bg-orange-500 text-white border-transparent',
  OUTER_APPLY: 'bg-orange-500 text-white border-transparent',
};

export const KIND_ARIA: Record<RenderJoinKind, string> = {
  INNER: 'Inner join',
  LEFT: 'Left join',
  RIGHT: 'Right join',
  FULL: 'Full join',
  CROSS: 'Cross join',
  CROSS_APPLY: 'Cross apply',
  OUTER_APPLY: 'Outer apply',
};

/**
 * One-liner tooltip per JOIN kind — makes the `KIND_BADGE_CLASS` color code
 * learnable instead of decorative. Applied to the kind badge in BOTH the
 * enabled dropdown (trigger + each `DropdownMenuItem`) and the disabled
 * scaffold, so the legend is visible whether or not editing is live.
 *
 * VQE Polish — Wave 0 primitive #7 (Advisor 2 §B3).
 */
export const KIND_TOOLTIPS: Record<RenderJoinKind, string> = {
  INNER: 'INNER JOIN — only rows with a match in both tables',
  LEFT: 'LEFT JOIN — all left rows, matched right rows or NULL',
  RIGHT: 'RIGHT JOIN — all right rows, matched left rows or NULL',
  FULL: 'FULL JOIN — all rows from both sides, unmatched side filled with NULL',
  CROSS: 'CROSS JOIN — Cartesian product of both sides, no ON predicate',
  CROSS_APPLY: 'CROSS APPLY — right side evaluated per left row, dropped when it returns nothing',
  OUTER_APPLY: 'OUTER APPLY — right side evaluated per left row, kept as NULL when it returns nothing',
};

const RENDER_TO_SR_EDIT: Record<RenderJoinKind, SrEditJoinKind> = {
  INNER: 'inner',
  LEFT: 'left',
  RIGHT: 'right',
  FULL: 'full',
  CROSS: 'cross',
  CROSS_APPLY: 'cross-apply',
  OUTER_APPLY: 'outer-apply',
};

export function renderKindToSrEditKind(kind: RenderJoinKind): SrEditJoinKind {
  return RENDER_TO_SR_EDIT[kind];
}

const UNIVERSAL_KINDS: readonly RenderJoinKind[] = ['INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS'];
const APPLY_KINDS: readonly RenderJoinKind[] = ['CROSS_APPLY', 'OUTER_APPLY'];

// MySQL doesn't support FULL OUTER JOIN, but the simulation surface (via
// UNION of LEFT + RIGHT) is out of scope for v1 — the user can still pick
// FULL and the engine will surface its own error if they execute it. v1
// keeps the dropdown choice list dialect-conservative for APPLY only,
// where the syntax is genuinely incompatible.
export function availableKindsForEngine(engine: SqlEngine): readonly RenderJoinKind[] {
  if (engine === 'mssql') {
    return [...UNIVERSAL_KINDS, ...APPLY_KINDS];
  }
  return UNIVERSAL_KINDS;
}
